/*
 * Carbisdale - AutoCorrect
 *
 * Generische Autokorrektur-Engine. Erstes Feature: Emoticon → Emoji.
 * Zweites Feature: Format-Makros (*, **, _, ~~, `) für RT-Modus.
 * Erweiterbar für zukünftige Autokorrektur-Patterns.
 */

// Emoji-Regeln (sortiert nach Trigger-Länge, longest first)
const EMOJI_RULES = [
    // 3-Zeichen-Trigger
    { trigger: ':-)', replacement: '😊' },
    { trigger: ':-(', replacement: '😞' },
    { trigger: ':-D', replacement: '😃' },
    { trigger: ':-P', replacement: '😛' },
    { trigger: ':-O', replacement: '😮' },
    { trigger: ':-*', replacement: '😘' },
    { trigger: ':-/', replacement: '😕' },
    { trigger: ':-|', replacement: '😐' },
    { trigger: ';-)', replacement: '😉' },
    { trigger: 'B-)', replacement: '😎' },
    // 2-Zeichen-Trigger
    { trigger: ':)', replacement: '🙂' },
    { trigger: ':(', replacement: '☹️' },
    { trigger: ':D', replacement: '😃' },
    { trigger: ':P', replacement: '😛' },
    { trigger: ':O', replacement: '😮' },
    { trigger: ':*', replacement: '😘' },
    { trigger: ';)', replacement: '😉' },
    { trigger: '<3', replacement: '❤️' },
]

// Format-Makro-Regeln (longest first → ** vor *, ~~ vor ~)
const FORMAT_MACRO_RULES = [
    { marker: '**', formatType: 'bold' },
    { marker: '~~', formatType: 'strikethrough' },
    { marker: '*', formatType: 'italic' },
    { marker: '_', formatType: 'underline' },
    { marker: '`', formatType: 'code' },
]

/**
 * AutoCorrect — Generische Autokorrektur-Engine.
 * Prüft Text vor dem Cursor auf bekannte Trigger und ersetzt sie.
 */
export class AutoCorrect {
    /**
     * @param {function} getDisplayMode - Gibt den aktuellen Display Mode zurück
     */
    constructor(getDisplayMode) {
        this._getDisplayMode = getDisplayMode
        this._manualOverride = null   // null = auto, true/false = manuell (für Menü-Switch)
        this._lastReplacement = null  // Tracking für Backspace-Undo
        this._rules = EMOJI_RULES
    }

    // Enabled-Logik: manueller Override > Display-Mode-Automatik
    get enabled() {
        if (this._manualOverride !== null) return this._manualOverride
        return this._getDisplayMode() !== 'NERD'
    }
    set enabled(value) { this._manualOverride = value }

    /**
     * Prüft ob Text vor dem Cursor einen Trigger enthält.
     * @param {string} text - Der gesamte Text
     * @param {number} offset - Cursor-Position (nach dem zuletzt eingefügten Zeichen)
     * @param {object} [options] - Zusätzliche Optionen für Format-Makros
     * @param {Set} [options.activeFormats] - Aktive Formate (für OFF-Trigger)
     * @returns {object|null} { kind: 'emoji', trigger, replacement, start }
     *                     | { kind: 'formatOn', marker, formatType, start }
     *                     | { kind: 'formatOff', marker, formatType, start }
     */
    check(text, offset, options = null) {
        // Format-Makros VOR dem enabled-Check (funktionieren auch im Nerd-Mode)
        if (options) {
            const formatResult = this._checkFormatMacro(text, offset, options.activeFormats)
            if (formatResult) return formatResult
        }

        // Emoji-Regeln nur wenn enabled (im Nerd-Mode deaktiviert)
        if (!this.enabled) return null
        for (const rule of this._rules) {
            const triggerLen = rule.trigger.length
            if (offset < triggerLen) continue

            const start = offset - triggerLen
            const candidate = text.substring(start, offset)
            if (candidate !== rule.trigger) continue

            // Wort-Grenze: davor muss Whitespace oder Textanfang stehen
            if (start > 0 && !/\s/.test(text[start - 1])) continue

            return { kind: 'emoji', trigger: rule.trigger, replacement: rule.replacement, start }
        }

        return null
    }

    /**
     * Prüft Format-Makros (ON und OFF).
     * ON: Marker am Wortanfang, gerade ein Non-WS-Zeichen getippt → Format aktivieren.
     * OFF: Marker gerade getippt, Format ist aktiv → Format deaktivieren.
     *
     * Longest-Match-First: ** vor *, ~~ vor ~.
     */
    _checkFormatMacro(text, offset, activeFormats) {
        if (offset < 2) return null  // Mindestens Marker + 1 Zeichen

        // OFF-Check zuerst (gerade getipptes Zeichen ist letztes Marker-Zeichen)
        // Longest-Match-First: ** vor * prüfen
        for (const rule of FORMAT_MACRO_RULES) {
            const { marker, formatType } = rule
            const markerLen = marker.length

            if (!activeFormats || !activeFormats.has(formatType)) continue
            if (offset < markerLen) continue

            // Marker prüfen: die letzten markerLen Zeichen müssen der Marker sein
            const candidate = text.substring(offset - markerLen, offset)
            if (candidate !== marker) continue

            // Davor muss Non-WS stehen (Format-Inhalt)
            if (offset - markerLen > 0 && !/\S/.test(text[offset - markerLen - 1])) continue

            return { kind: 'formatOff', marker, formatType, start: offset - markerLen }
        }

        // ON-Check: Marker + gerade getipptes Non-WS-Zeichen
        const lastChar = text[offset - 1]
        if (/\s/.test(lastChar)) return null  // Letztes Zeichen muss Non-WS sein
        // Getipptes Zeichen darf kein Marker-Zeichen sein (sonst könnte es Teil eines längeren Markers werden,
        // bzw. bei Backtick selbst neuer ON-Anker statt Inhalt)
        if (lastChar === '*' || lastChar === '_' || lastChar === '~' || lastChar === '`') return null

        for (const rule of FORMAT_MACRO_RULES) {
            const { marker, formatType } = rule
            const markerLen = marker.length

            // Format darf nicht schon aktiv sein (sonst wäre es ein OFF)
            if (activeFormats && activeFormats.has(formatType)) continue

            // Marker vor dem gerade getippten Zeichen
            const markerStart = offset - 1 - markerLen
            if (markerStart < 0) continue

            const candidate = text.substring(markerStart, markerStart + markerLen)
            if (candidate !== marker) continue

            // Wort-Grenze: davor muss WS oder Textanfang stehen
            if (markerStart > 0 && !/\s/.test(text[markerStart - 1])) continue

            return { kind: 'formatOn', marker, formatType, start: markerStart }
        }

        return null
    }

    /** @returns {object|null} Letztes Replacement-Info */
    get lastReplacement() { return this._lastReplacement }

    /** @param {object|null} v */
    set lastReplacement(v) { this._lastReplacement = v }

    /** Setzt das letzte Replacement zurück */
    clearLastReplacement() { this._lastReplacement = null }
}
